import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSeoMeta } from "@unhead/react";
import { Bitcoin, Check, Copy, CreditCard, ExternalLink } from "lucide-react";

import { SiteHeader } from "@/components/cashcraft/SiteHeader";
import { SiteFooter } from "@/components/cashcraft/SiteFooter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QRCodeCanvas } from "@/components/ui/qrcode";
import { useToast } from "@/hooks/useToast";
import { useBchPrice, usdToBch } from "@/hooks/useBchPrice";
import { cn } from "@/lib/utils";
import {
  BCH_ADDRESS,
  DEPOSIT_RATE,
  PAYPAL_LINK,
  PRICING_TIERS,
  TIER_USD,
} from "@/lib/cashcraft";

/** Strip the "bitcoincash:" scheme for display. */
const displayAddress = BCH_ADDRESS.replace(/^bitcoincash:/, "");

function useCopy() {
  const { toast } = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      toast({ title: "Copied to clipboard" });
      setTimeout(() => setCopied((c) => (c === label ? null : c)), 2000);
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  };
  return { copied, copy };
}

const Pay = () => {
  useSeoMeta({
    title: "Pay in Bitcoin Cash · BCH Launch",
    description: "Send your Bitcoin Cash payment to BCH Launch.",
  });

  const [params] = useSearchParams();
  const { data: bchUsd } = useBchPrice();
  const { copied, copy } = useCopy();

  const initialTier = params.get("tier") ?? "growth";
  const isDeposit = params.get("deposit") === "1";
  const orderId = params.get("order") ?? "";
  const [selectedTier, setSelectedTier] = useState<string>(
    TIER_USD[initialTier] ? initialTier : "growth",
  );
  const [customUsd, setCustomUsd] = useState<string>("");

  // Full tier/custom price before any deposit split.
  const fullUsd = useMemo(() => {
    if (selectedTier === "custom") {
      const n = Number(customUsd);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }
    return TIER_USD[selectedTier] ?? 0;
  }, [selectedTier, customUsd]);

  // Deposit mode only applies to fixed tiers, not custom amounts.
  const depositMode = isDeposit && selectedTier !== "custom";

  const usdAmount = useMemo(() => {
    if (depositMode) return Math.round(fullUsd * DEPOSIT_RATE);
    return fullUsd;
  }, [depositMode, fullUsd]);

  const bchAmount = useMemo(() => {
    if (!bchUsd || !usdAmount) return "";
    return usdToBch(usdAmount, bchUsd);
  }, [bchUsd, usdAmount]);

  // BIP21-style URI so wallets can prefill the amount.
  const paymentUri = useMemo(() => {
    if (bchAmount && Number(bchAmount) > 0) {
      return `${BCH_ADDRESS}?amount=${bchAmount}`;
    }
    return BCH_ADDRESS;
  }, [bchAmount]);

  const explorerUrl = `https://blockchair.com/bitcoin-cash/address/${displayAddress}`;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <div className="container mx-auto max-w-3xl px-4 py-14 sm:px-6">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
              <Bitcoin className="h-4 w-4" />
              {depositMode ? "50% deposit to start" : "Peer-to-peer payment"}
            </div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              {depositMode ? "Pay your deposit" : "Pay in Bitcoin Cash"}
            </h1>
            <p className="mt-3 text-muted-foreground">
              {depositMode
                ? "Send your 50% deposit in BCH to lock in your project. The balance is due on delivery."
                : "Send BCH directly to BCH Launch — no middlemen, no processors."}
            </p>
          </div>

          {/* Amount selection */}
          <Card className="mb-6 border-border/60">
            <CardHeader>
              <CardTitle className="text-lg">Choose an amount</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-4">
                {PRICING_TIERS.map((tier) => (
                  <button
                    key={tier.id}
                    type="button"
                    onClick={() => setSelectedTier(tier.id)}
                    className={cn(
                      "rounded-xl border p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      selectedTier === tier.id
                        ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                        : "border-border hover:border-primary/50 hover:bg-accent/50",
                    )}
                  >
                    <div className="text-sm font-semibold">{tier.name}</div>
                    <div className="text-primary">{tier.priceUsd}</div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setSelectedTier("custom")}
                  className={cn(
                    "rounded-xl border p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    selectedTier === "custom"
                      ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                      : "border-border hover:border-primary/50 hover:bg-accent/50",
                  )}
                >
                  <div className="text-sm font-semibold">Custom</div>
                  <div className="text-primary">$…</div>
                </button>
              </div>

              {selectedTier === "custom" && (
                <div className="space-y-2">
                  <Label htmlFor="custom">Custom amount (USD)</Label>
                  <Input
                    id="custom"
                    inputMode="decimal"
                    placeholder="e.g. 350"
                    value={customUsd}
                    onChange={(e) => setCustomUsd(e.target.value)}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment method: Card / USD via PayPal */}
          <Card className="mb-4 border-border/60">
            <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
              <div>
                <div className="flex items-center gap-2 font-semibold">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Pay with card (USD)
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Pay securely via PayPal — no PayPal account needed, cards
                  accepted.
                </p>
                {usdAmount > 0 && (
                  <p className="mt-2 text-sm">
                    On the PayPal page, enter{" "}
                    <strong className="text-foreground">${usdAmount}</strong>
                    {depositMode ? " (your 50% deposit)" : ""}.
                  </p>
                )}
              </div>
              <Button
                asChild
                size="lg"
                className="shrink-0 rounded-full font-semibold"
              >
                <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                  Pay ${usdAmount || ""} with card
                  <ExternalLink className="ml-1.5 h-4 w-4" />
                </a>
              </Button>
            </CardContent>
          </Card>

          <div className="mb-4 flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            or pay peer-to-peer in Bitcoin Cash
            <span className="h-px flex-1 bg-border" />
          </div>

          {/* Payment method: Bitcoin Cash */}
          <Card className="border-primary/30">
            <CardContent className="grid gap-8 p-6 sm:p-8 md:grid-cols-[auto_1fr] md:items-center">
              <div className="flex flex-col items-center gap-3">
                <div className="rounded-xl bg-white p-3">
                  <QRCodeCanvas value={paymentUri} size={200} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Scan with any BCH wallet
                </p>
              </div>

              <div className="space-y-5">
                <div>
                  <div className="text-sm text-muted-foreground">
                    {depositMode ? "Deposit due now (50%)" : "Amount due"}
                  </div>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-3xl font-extrabold">
                      {usdAmount ? `$${usdAmount}` : "—"}
                    </span>
                    <span className="text-lg font-semibold text-primary">
                      {bchAmount ? `≈ ${bchAmount} BCH` : ""}
                    </span>
                  </div>
                  {depositMode && fullUsd > 0 && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Total ${fullUsd} · balance of ${fullUsd - usdAmount} due on
                      delivery
                    </div>
                  )}
                  <div className="mt-1 text-xs text-muted-foreground">
                    {bchUsd
                      ? `Live rate: 1 BCH ≈ $${bchUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                      : "Fetching live rate…"}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Send to this address</Label>
                  <div className="flex items-stretch gap-2">
                    <code className="flex-1 break-all rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs">
                      {displayAddress}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label="Copy address"
                      onClick={() => copy(displayAddress, "addr")}
                    >
                      {copied === "addr" ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {bchAmount && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs"
                      onClick={() => copy(bchAmount, "amt")}
                    >
                      {copied === "amt" ? (
                        <Check className="mr-1 h-3 w-3 text-primary" />
                      ) : (
                        <Copy className="mr-1 h-3 w-3" />
                      )}
                      Copy amount ({bchAmount} BCH)
                    </Button>
                  )}
                </div>

                <a
                  href={explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  View address on block explorer
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 rounded-xl border border-dashed border-border bg-muted/20 p-5 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">After you pay</p>
            <p className="mt-1">
              Paid with card? You're all set — we'll see it and get started.
              Paid in BCH? Send us the transaction ID (or reply to your project
              thread) so we can confirm it on-chain.
              {depositMode
                ? " The remaining balance is due once your site is ready to launch."
                : ""}
            </p>
            {orderId && (
              <a
                href={`/track/${orderId}`}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                Track your project status →
              </a>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Pay;
